/**
 * CNS Renderer — renders Flux view trees as interactive React components.
 *
 * Design system: uses gravity CSS variables for seamless visual integration
 * with the chat UI. Supports light/dark mode automatically.
 *
 * Responsive: adapts to mobile viewports (FluxAppScreen is standalone,
 * or embedded in chat via iframe/DynamicBlocks).
 */
import './flux-cns.css';
import { useCallback, useMemo, useState } from 'react';
import { sanitizeRenderedHtml } from './sanitizeRenderedMarkup';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CNSView {
  type: string;
  id?: string;
  children?: CNSView[];
  // Text
  content?: string;
  style?: string; // h1 h2 h3 body caption label
  align?: string; // left center right
  // Button
  label?: string;
  action?: CNSAction;
  variant?: string; // primary secondary outline danger ghost
  disabled?: string;
  icon?: string; // emoji prefix
  // TextField
  placeholder?: string;
  field?: string;
  multiline?: boolean;
  // List
  items?: string;
  item_view?: CNSView;
  itemTemplate?: CNSView;
  empty_text?: string;
  emptyText?: string;
  // Progress
  value?: string;
  max?: string;
  // Badge
  text?: string;
  color?: string;
  // Image
  src?: string;
  alt?: string;
  // Toggle
  checked?: string;
  on_label?: string;
  off_label?: string;
  // Divider / Spacer
  height?: string;
  // visible (conditional)
  visible?: string;
}

export interface CNSAction {
  type: 'dispatch_intent';
  name: string;
  payload?: Record<string, unknown>;
}

export interface RendererProps {
  view: CNSView;
  state: Record<string, unknown>;
  onDispatchIntent: (intentName: string, payload: Record<string, unknown>) => Promise<void>;
  sessionId: string;
  isDispatching?: boolean;
  /** Participants list for displaying names */
  participants?: string[];
}

// ---------------------------------------------------------------------------
// State Interpolation
// ---------------------------------------------------------------------------

/**
 * Interpolate template strings against state.
 *
 * Supports two syntaxes (both may appear in the same string):
 *   - Server-side / Starlark: {{state.key}}, {{private.key}}
 *   - Client-side extended:   ${state.key}, ${expr ? a : b}, ${a || b}
 *
 * The {{…}} syntax is what Starlark handlers produce and what the Go
 * server-side renderer (describe.go / render.go) resolves. Since Firestore
 * stores the raw CNS template, the client must resolve it too.
 */
export function interpolate(
  template: string | undefined,
  state: Record<string, unknown>,
): string {
  if (!template) return '';

  // Pass 1: resolve {{state.key}} and {{private.key}} (server-style templates).
  let result = template.replace(/\{\{(state|private)\.([^}]+)\}\}/g, (_, ns: string, key: string) => {
    if (ns === 'private') {
      // Private state is not available on the public Firestore doc.
      return '';
    }
    const val = resolvePath(key, state);
    if (val === undefined || val === null) return '';
    return String(val);
  });

  // Pass 2: resolve ${expr} (extended client-side templates).
  result = result.replace(/\$\{([^}]+)\}/g, (_, expr: string) => {
    const trimmed = expr.trim();

    // Ternary: ${state.opened ? state.fortune : 'Click to reveal'}
    const ternaryMatch = trimmed.match(/^(.+?)\s*\?\s*(.+?)\s*:\s*(.+)$/);
    if (ternaryMatch) {
      const cond = resolvePath(ternaryMatch[1]!.trim(), state);
      const truthy = cond && cond !== 0;
      const branch = truthy ? ternaryMatch[2]!.trim() : ternaryMatch[3]!.trim();
      if (branch.startsWith("'") || branch.startsWith('"'))
        return branch.slice(1, -1);
      const resolved = resolvePath(branch, state);
      return resolved !== undefined && resolved !== null ? String(resolved) : '';
    }

    // Default: ${state.value || 'fallback'}
    const orParts = trimmed.split('||').map((s: string) => s.trim());
    for (const part of orParts) {
      if (part.startsWith("'") || part.startsWith('"')) return part.slice(1, -1);
      const val = resolvePath(part, state);
      if (val !== undefined && val !== null && val !== '') return String(val);
    }

    // Zero is a valid display value.
    const val = resolvePath(orParts[0] ?? '', state);
    if (val === 0) return '0';

    return '';
  });

  return result;
}

export function resolvePath(path: string, state: Record<string, unknown>): unknown {
  // Strip "state." or "ctx." prefix.
  let cleanPath = path;
  if (cleanPath.startsWith('state.')) cleanPath = cleanPath.slice(6);
  else if (cleanPath.startsWith('ctx.')) return undefined; // ctx is server-side only

  // Handle bracket notation: items[0], ratings[state.selection]
  const parts: string[] = [];
  let current = '';
  let bracketDepth = 0;

  for (const char of cleanPath) {
    if (char === '[') {
      if (current) parts.push(current);
      current = '';
      bracketDepth++;
    } else if (char === ']') {
      bracketDepth--;
      if (bracketDepth === 0) {
        // Resolve bracket content as either a number or state path.
        const bracketContent = current.trim();
        if (/^\d+$/.test(bracketContent)) {
          parts.push(bracketContent);
        } else {
          const resolved = resolvePath(bracketContent, state);
          parts.push(String(resolved ?? ''));
        }
        current = '';
      }
    } else if (char === '.' && bracketDepth === 0) {
      if (current) parts.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  if (current) parts.push(current);

  let obj: unknown = state;
  for (const part of parts) {
    if (obj === null || obj === undefined) return undefined;
    if (Array.isArray(obj)) {
      const idx = parseInt(part, 10);
      if (isNaN(idx)) return undefined;
      obj = obj[idx];
    } else if (typeof obj === 'object') {
      obj = (obj as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return obj;
}

/**
 * Evaluate a visibility expression — truthy check on a state value.
 *
 * Supports:
 *   - {{state.key}}  — server-style template (resolved, then checked for "true"/"1")
 *   - state.key      — bare path (resolved via resolvePath)
 *   - boolean values  — direct truthiness
 */
function isVisible(expr: string | undefined, state: Record<string, unknown>): boolean {
  if (expr === undefined || expr === null) return true;

  // Boolean (from Firestore deserialization).
  if (typeof expr === 'boolean') return expr;

  // Server-style template: {{state.key}}
  const tmplMatch = expr.match(/^\{\{state\.([^}]+)\}\}$/);
  if (tmplMatch) {
    const val = resolvePath(tmplMatch[1]!, state);
    // Match Go isVisible: resolved string must be "true" or "1".
    return val === true || val === 'true' || val === '1' || val === 1;
  }

  // Bare path: state.key or just key
  const val = resolvePath(expr, state);
  return !!val;
}

// ---------------------------------------------------------------------------
// Component: Column
// ---------------------------------------------------------------------------

function CNSColumn({ view, ...rest }: RendererProps) {
  return (
    <div className="flux-col">
      {view.children?.map((child, i) => (
        <CNSNode key={child.id ?? i} view={child} {...rest} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component: Row
// ---------------------------------------------------------------------------

function CNSRow({ view, ...rest }: RendererProps) {
  return (
    <div className="flux-row">
      {view.children?.map((child, i) => (
        <CNSNode key={child.id ?? i} view={child} {...rest} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component: Text
// ---------------------------------------------------------------------------

function CNSText({ view, state }: RendererProps) {
  const content = interpolate(view.content, state);
  const cls = `flux-text flux-text--${view.style ?? 'body'} ${
    view.align ? `flux-text--${view.align}` : ''
  }`;
  if (view.style === 'h1') return <h2 className={cls}>{content}</h2>;
  if (view.style === 'h2') return <h3 className={cls}>{content}</h3>;
  return <p className={cls}>{content}</p>;
}

// ---------------------------------------------------------------------------
// Component: Button
// ---------------------------------------------------------------------------

function interpolateActionPayload(value: unknown, state: Record<string, unknown>): unknown {
  if (typeof value === 'string') {
    return interpolate(value, state);
  }
  if (Array.isArray(value)) {
    return value.map((item) => interpolateActionPayload(item, state));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, itemValue]) => [
        key,
        interpolateActionPayload(itemValue, state),
      ]),
    );
  }
  return value;
}

function CNSButton({ view, state, onDispatchIntent, isDispatching }: RendererProps) {
  const label = interpolate(view.label, state);
  const [localBusy, setLocalBusy] = useState(false);

  const handleClick = useCallback(async () => {
    if (!view.action || view.action.type !== 'dispatch_intent') return;
    setLocalBusy(true);
    try {
      const payload = interpolateActionPayload(
        view.action.payload ?? {},
        state,
      ) as Record<string, unknown>;
      await onDispatchIntent(view.action.name, payload);
    } finally {
      setLocalBusy(false);
    }
  }, [view.action, state, onDispatchIntent]);

  const busy = localBusy || isDispatching;
  const variant = view.variant ?? 'primary';

  return (
    <button
      className={`flux-btn flux-btn--${variant} ${busy ? 'flux-btn--loading' : ''}`}
      onClick={handleClick}
      disabled={busy}
    >
      {view.icon && <span className="flux-btn-icon">{view.icon}</span>}
      <span className="flux-btn-label">{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Component: TextField
// ---------------------------------------------------------------------------

function CNSTextField({ view, state, onDispatchIntent }: RendererProps) {
  const placeholder = interpolate(view.placeholder, state);
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!view.action || !value.trim()) return;
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        ...(interpolateActionPayload(view.action.payload ?? {}, state) as Record<string, unknown>),
        value: value.trim(),
      };
      if (view.field) payload[view.field] = value.trim();
      await onDispatchIntent(view.action.name, payload);
      setValue('');
    } finally {
      setSubmitting(false);
    }
  }, [view.action, view.field, state, value, onDispatchIntent]);

  return (
    <div className="flux-input-group">
      {view.multiline ? (
        <textarea
          className="flux-input flux-textarea"
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={3}
        />
      ) : (
        <input
          className="flux-input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
        />
      )}
      {view.action && (
        <button
          className="flux-input-submit"
          onClick={handleSubmit}
          disabled={submitting || !value.trim()}
        >
          {submitting ? '...' : '→'}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component: List
// ---------------------------------------------------------------------------

function CNSList({ view, state, ...rest }: RendererProps) {
  // Items can be: "state.key", "{{state.key}}", or a direct array.
  let itemsPath = view.items ?? '';
  // Strip {{state.xxx}} template wrapper if present.
  const tmpl = itemsPath.match(/^\{\{state\.([^}]+)\}\}$/);
  if (tmpl) itemsPath = tmpl[1]!;
  else itemsPath = itemsPath.replace(/^state\./, '');

  const items = useMemo(
    () => (resolvePath(itemsPath, state) as unknown[]) ?? [],
    [itemsPath, state],
  );
  const emptyText = interpolate(view.empty_text ?? view.emptyText, state) || 'No items yet';
  const itemView = view.item_view ?? view.itemTemplate;

  if (items.length === 0) {
    return <p className="flux-text flux-text--caption flux-empty">{emptyText}</p>;
  }

  return (
    <div className="flux-list">
      {items.map((item, i) => {
        if (itemView) {
          const itemState = { ...state, item, index: i };
          return <CNSNode key={i} view={itemView} state={itemState} {...rest} />;
        }
        return (
          <div key={i} className="flux-list-item">
            <span className="flux-list-bullet">•</span>
            <span className="flux-list-text">
              {typeof item === 'string' ? item : JSON.stringify(item)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component: Progress
// ---------------------------------------------------------------------------

function CNSProgress({ view, state }: RendererProps) {
  const value = Number(interpolate(view.value, state)) || 0;
  const max = Number(interpolate(view.max, state)) || 100;
  const pct = Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div className="flux-progress-wrap">
      <div className="flux-progress-track">
        <div
          className="flux-progress-fill"
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={max}
        />
      </div>
      <span className="flux-progress-label">{value}/{max}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component: Badge
// ---------------------------------------------------------------------------

function CNSBadge({ view, state }: RendererProps) {
  const text = interpolate(view.text ?? view.content, state);
  const colorClass = view.color ? `flux-badge--${view.color}` : '';
  return <span className={`flux-badge ${colorClass}`}>{text}</span>;
}

// ---------------------------------------------------------------------------
// Component: Image
// ---------------------------------------------------------------------------

function CNSImage({ view, state }: RendererProps) {
  const src = interpolate(view.src, state);
  const alt = interpolate(view.alt ?? view.content, state) || 'Image';
  if (!src) return null;
  return <img className="flux-image" src={src} alt={alt} loading="lazy" />;
}

// ---------------------------------------------------------------------------
// Component: Html — sanitized, non-interactive rendered markup
// ---------------------------------------------------------------------------

function CNSHtml({ view, state }: RendererProps) {
  const content = useMemo(
    () => sanitizeRenderedHtml(interpolate(view.content, state)),
    [state, view.content],
  );
  if (!content) return null;
  return <div className="flux-html" inert={true} dangerouslySetInnerHTML={{ __html: content }} />;
}

// ---------------------------------------------------------------------------
// Component: Divider
// ---------------------------------------------------------------------------

function CNSDivider() {
  return <hr className="flux-divider" />;
}

// ---------------------------------------------------------------------------
// Component: Spacer
// ---------------------------------------------------------------------------

function CNSSpacer({ view }: RendererProps) {
  const h = parseInt(view.height ?? '16', 10);
  return <div style={{ height: `${h}px` }} />;
}

// ---------------------------------------------------------------------------
// Node dispatcher
// ---------------------------------------------------------------------------

export function CNSNode(props: RendererProps) {
  const { view, state } = props;

  // Visibility gate.
  if (!isVisible(view.visible, state)) return null;

  switch (view.type) {
    case 'Column': return <CNSColumn {...props} />;
    case 'Row': return <CNSRow {...props} />;
    case 'Text': return <CNSText {...props} />;
    case 'Button': return <CNSButton {...props} />;
    case 'TextField': return <CNSTextField {...props} />;
    case 'List': return <CNSList {...props} />;
    case 'Progress': return <CNSProgress {...props} />;
    case 'Badge': return <CNSBadge {...props} />;
    case 'Image': return <CNSImage {...props} />;
    case 'Html': return <CNSHtml {...props} />;
    case 'Divider': return <CNSDivider />;
    case 'Spacer': return <CNSSpacer {...props} />;
    default:
      // Unknown component — render children if present.
      if (view.children?.length) return <CNSColumn {...props} />;
      return null;
  }
}
